import { useMemo, useRef, useState } from 'react';
import { createEditor } from 'slate';
import { withReact, Slate, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import withEmailEditor from './withEmailEditor';
import EmailContext from './EmailContext';
import { debounce } from 'lodash';
import useEditorProps from '@hooks/useEditorProps';
import { getStructure } from '@Utils/dev';
import { renderHtml } from '@Utils/serializeEmail';
import { htmlToImage } from '@Utils/htmlToImage';

type saveStatus = 'saving' | 'saved' | 'pending';

function EmailEditorProvider({ children }) {
    const [hoveringPath, setHoveringPath] = useState(null);
    const [iframeTarget, setIframeTarget] = useState(null);
    const [selectedPath, setSelectedPath] = useState(null);
    const [draggedElement, setDraggedElement] = useState(null);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkSelection, setLinkSelection] = useState(null);
    const [saveStatus, setSaveStatus] = useState<saveStatus>('saved');
    const iframeRef = useRef(null);
    const { initialData, onSave, title, saveWithHtml, saving } =
        useEditorProps();

    const editor = useMemo(
        () => withEmailEditor(withReact(withHistory(createEditor()))),
        [],
    );

    const buildTemplateObject = async () => {
        try {
            const htmlString = await renderHtml(editor, {});

            const htmlElement = ReactEditor.toDOMNode(
                editor,
                editor.children[0],
            );

            const backgroundColor = htmlElement.lastChild.style.backgroundColor;
            htmlElement.lastChild.style.backgroundColor = null;
            console.log(backgroundColor);

            const thumbnail = await htmlToImage(htmlElement, {
                size: 400,
            });
            htmlElement.lastChild.style.backgroundColor = backgroundColor;

            const template = {
                title,
                thumbnail,
                template: editor.children,
            };

            if (saveWithHtml) {
                template.html = htmlString;
            }

            return template;
        } catch (error) {
            console.error('Error building template:', error);
            return null; // Or handle the error as needed
        }
    };

    const saveEmail = () =>
        buildTemplateObject()
            .then(template => onSave(template))
            .then(() =>
                setSaveStatus(prev => (prev === 'pending' ? 'saved' : prev)),
            );

    const debouncedSave = useRef(debounce(saveEmail, 5000)).current;

    return (
        <Slate
            editor={editor}
            initialValue={initialData}
            // initialValue={initialValue}
            onChange={value => {
                setSaveStatus('pending');
                console.log(getStructure(value[0]));
                debouncedSave();
            }}
        >
            <EmailContext.Provider
                value={{
                    iframeTarget,
                    setIframeTarget,
                    hoveringPath,
                    setHoveringPath,
                    selectedPath,
                    setSelectedPath,
                    draggedElement,
                    setDraggedElement,
                    iframeRef,
                    linkOpen,
                    setLinkOpen,
                    linkSelection,
                    setLinkSelection,
                    saveStatus: saving ? 'saving' : saveStatus,
                    saveEmail,
                }}
            >
                {children}
            </EmailContext.Provider>
        </Slate>
    );
}

export default EmailEditorProvider;
